import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Rocket, CalendarDays, ArrowRight, ShoppingCart, Wrench, Handshake,
  Newspaper, Check, Send, MapPin, Phone, Mail, Globe, Clock, Building2,
  Linkedin, Twitter, Youtube, Instagram,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CALENDLY_DEMO_URL, getWhatsAppUrl } from "@/constants/links";

// lucide's brand icons are deprecated, so WhatsApp ships as an inline SVG.
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";
import FinalCTA from "@/components/home/FinalCTA";
import HeroIntro from "@/components/home/HeroIntro";
import heroBg from "@/assets/home-decor-categories.jpg";

const CONTACT_EMAIL = "support@neivai.ai";

const HELP_OPTIONS = [
  {
    icon: ShoppingCart,
    title: "Sales",
    body: "Learn how NEIV can help your business increase customer confidence and improve conversions.",
    bestFor: ["Product demonstrations", "Pricing inquiries", "Enterprise plans", "Custom solutions", "Partnerships"],
    cta: "Contact Sales",
  },
  {
    icon: Wrench,
    title: "Support",
    body: "Already using NEIV? Our support team is ready to assist with technical questions, account issues, and troubleshooting.",
    cta: "Get Support",
  },
  {
    icon: Handshake,
    title: "Partnerships",
    body: "Interested in collaborating with NEIV? We're always looking to work with retailers, manufacturers, technology providers, agencies, and strategic partners.",
    cta: "Become a Partner",
  },
  {
    icon: Newspaper,
    title: "Media & Press",
    body: "For interviews, press inquiries, speaking opportunities, or media resources.",
    cta: "Contact Media Team",
  },
];

const INDUSTRIES = [
  "Furniture", "Paint", "Tile & Flooring", "Interior Design",
  "Kitchen", "Bathroom", "Real Estate", "Home Décor", "Other",
];

const INQUIRY_TYPES = [
  "Product Demo", "Pricing", "Technical Support",
  "Enterprise Solution", "Partnership", "General Inquiry",
];

const DEMO_COVERS = [
  "Platform overview",
  "AI visualization workflow",
  "Product catalog management",
  "Business use cases",
  "Questions & answers",
];

const BUSINESS_HOURS = [
  { day: "Monday – Friday", hours: "9:00 AM – 6:00 PM" },
  { day: "Saturday", hours: "10:00 AM – 2:00 PM" },
  { day: "Sunday", hours: "Closed" },
];

// Placeholder office details — replace with real information when available.
const OFFICE_DETAILS = [
  { icon: MapPin, label: "Office Address" },
  { icon: Phone, label: "Phone Number" },
  { icon: Mail, label: "Email Address" },
  { icon: Globe, label: "Website" },
];

const FAQS = [
  {
    q: "How quickly will I receive a response?",
    a: "We aim to respond to all inquiries within one business day.",
  },
  {
    q: "Can I request a personalized product demo?",
    a: "Yes. Our team offers live demonstrations tailored to your industry and business requirements.",
  },
  {
    q: "Do you provide enterprise support?",
    a: "Yes. Enterprise customers receive dedicated onboarding, implementation guidance, and priority support.",
  },
  {
    q: "Can I integrate NEIV with my existing systems?",
    a: "Yes. NEIV provides APIs and integration options for supported plans.",
  },
];

const SOCIALS = [
  { icon: Linkedin, label: "LinkedIn", href: "#" },
  { icon: Twitter, label: "X (Twitter)", href: "#" },
  { icon: Youtube, label: "YouTube", href: "#" },
  { icon: Instagram, label: "Instagram", href: "#" },
];

const EMPTY_FORM = {
  name: "", email: "", company: "", phone: "",
  country: "", industry: "", inquiry: "", message: "",
};

const inputClass =
  "w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-base text-neutral-800 outline-none transition-colors placeholder:text-neutral-400 focus:border-primary focus:ring-2 focus:ring-primary/20";

function Field({ label, optional, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-semibold text-neutral-700">
        {label}
        {optional && <span className="ml-1 font-normal text-neutral-400">(Optional)</span>}
      </label>
      {children}
    </div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`${form.inquiry || "Inquiry"} — ${form.name}${form.company ? ` (${form.company})` : ""}`);
    const body = encodeURIComponent(
      [
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Company: ${form.company}`,
        form.phone && `Phone: ${form.phone}`,
        `Country / Region: ${form.country}`,
        `Industry: ${form.industry}`,
        `Inquiry type: ${form.inquiry}`,
        "",
        form.message,
      ]
        .filter(Boolean)
        .join("\n")
    );
    window.open(`mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`);
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-neutral-50 pb-16 pt-28 md:pb-20 md:pt-36">
          {/* Right-side image panel */}
          <div className="pointer-events-none absolute right-0 top-0 hidden h-full w-[45%] lg:block" aria-hidden="true">
            <img
              src={heroBg}
              alt=""
              className="h-full w-full object-cover saturate-[1.35] contrast-[1.08] brightness-[1.03]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-neutral-50/80 via-neutral-50/10 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-neutral-50/10" />
          </div>

          <div className="relative mx-auto max-w-6xl px-4 md:px-8">
            <div className="max-w-xl">
              <HeroIntro eyebrow="Contact Us" heading="Let's build better buying experiences together">
                <p className="mt-5 text-lg leading-relaxed text-neutral-500">
                  Whether you're exploring NEIV for the first time, looking for a
                  product demonstration, or interested in an enterprise
                  partnership, our team is here to help.
                </p>
                <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                  <a
                    href={CALENDLY_DEMO_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/20 transition-all hover:bg-primary/90 sm:w-auto"
                  >
                    <CalendarDays className="h-4 w-4" />
                    Schedule a Demo
                  </a>
                  <Link
                    to="/signup"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 px-8 py-3.5 text-base font-semibold text-neutral-800 transition-all hover:border-neutral-900 sm:w-auto"
                  >
                    <Rocket className="h-4 w-4" />
                    Start Free
                  </Link>
                </div>
              </HeroIntro>
            </div>
          </div>
        </section>

        {/* How can we help */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">How Can We Help?</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Choose the option that best describes your inquiry
              </h2>
            </div>
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {HELP_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <div
                    key={option.title}
                    className="flex flex-col rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03] transition-shadow hover:shadow-md"
                  >
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-5 text-xl font-bold text-neutral-900">{option.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-neutral-500">{option.body}</p>
                    {option.bestFor && (
                      <>
                        <p className="mt-4 text-xs font-bold uppercase tracking-widest text-neutral-400">
                          Best for
                        </p>
                        <ul className="mt-2 space-y-1.5">
                          {option.bestFor.map((item) => (
                            <li key={item} className="flex items-start gap-2 text-sm text-neutral-600">
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    <a
                      href="#contact-form"
                      className="group mt-auto inline-flex items-center gap-1.5 pt-6 text-sm font-semibold text-primary"
                    >
                      {option.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact form + book a demo */}
        <section id="contact-form" className="bg-neutral-50 py-24">
          <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-12 px-4 md:px-8 lg:grid-cols-5">
            {/* Form */}
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03] md:p-10 lg:col-span-3">
              {sent ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <Send className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="mt-5 text-2xl font-bold text-neutral-900">Message sent!</h3>
                  <p className="mt-2 max-w-xs leading-relaxed text-neutral-500">
                    Thanks for reaching out. We'll get back to you within one business day.
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm(EMPTY_FORM); }}
                    className="mt-6 rounded-lg border border-neutral-300 px-6 py-2.5 text-sm font-semibold text-neutral-800 transition-colors hover:border-neutral-900"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-neutral-900 md:text-3xl">Send us a message</h2>
                  <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <Field label="Full Name">
                        <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputClass} />
                      </Field>
                      <Field label="Business Email">
                        <input type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputClass} />
                      </Field>
                      <Field label="Company Name">
                        <input required value={form.company} onChange={set("company")} placeholder="Company name" className={inputClass} />
                      </Field>
                      <Field label="Phone Number" optional>
                        <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 " className={inputClass} />
                      </Field>
                      <Field label="Country / Region">
                        <input required value={form.country} onChange={set("country")} placeholder="Country or region" className={inputClass} />
                      </Field>
                      <Field label="Industry">
                        <select required value={form.industry} onChange={set("industry")} className={inputClass}>
                          <option value="" disabled>Select industry</option>
                          {INDUSTRIES.map((industry) => (
                            <option key={industry} value={industry}>{industry}</option>
                          ))}
                        </select>
                      </Field>
                    </div>
                    <Field label="How Can We Help?">
                      <select required value={form.inquiry} onChange={set("inquiry")} className={inputClass}>
                        <option value="" disabled>Select inquiry type</option>
                        {INQUIRY_TYPES.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Message">
                      <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={set("message")}
                        placeholder="Tell us more about your business and how we can help."
                        className={`${inputClass} resize-none`}
                      />
                    </Field>
                    <button
                      type="submit"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary/90"
                    >
                      <Send className="h-4 w-4" />
                      Send Message
                    </button>
                    <p className="text-center text-xs text-neutral-400">
                      We typically respond within one business day.
                    </p>
                  </form>
                </>
              )}
            </div>

            {/* Book a demo + office info */}
            <div className="space-y-8 lg:col-span-2">
              <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03]">
                <p className="text-sm font-bold uppercase tracking-widest text-primary">Book a Personalized Demo</p>
                <h3 className="mt-3 text-xl font-bold text-neutral-900">
                  See NEIV in action with a guided demonstration tailored to your business
                </h3>
                <p className="mt-4 text-xs font-bold uppercase tracking-widest text-neutral-400">
                  During the demo, we'll cover
                </p>
                <ul className="mt-3 space-y-2">
                  {DEMO_COVERS.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-neutral-700">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                      <span className="text-sm font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={CALENDLY_DEMO_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-primary/90"
                >
                  <CalendarDays className="h-4 w-4" />
                  Schedule a Demo
                </a>
              </div>

              {/* WhatsApp */}
              <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03]">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366]/10">
                    <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-neutral-900">Prefer WhatsApp?</h3>
                    <p className="text-sm text-neutral-500">Chat with our team for quick questions.</p>
                  </div>
                </div>
                <a
                  href={getWhatsAppUrl("Hi NEIV team! I found you through your website and I have a question.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#25D366] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#1EBE5D]"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </div>

              <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm shadow-neutral-900/[0.03]">
                <p className="text-sm font-bold uppercase tracking-widest text-primary">Headquarters</p>
                <div className="mt-3 flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Building2 className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">NEIV Technologies</h3>
                </div>
                {/* Placeholder details — replace when available */}
                <ul className="mt-5 space-y-3">
                  {OFFICE_DETAILS.map((detail) => {
                    const Icon = detail.icon;
                    return (
                      <li key={detail.label} className="flex items-center gap-3 text-sm text-neutral-500">
                        <Icon className="h-4 w-4 shrink-0 text-primary" />
                        {detail.label}
                      </li>
                    );
                  })}
                </ul>

                <div className="mt-7 border-t border-neutral-100 pt-6">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-neutral-400">
                    <Clock className="h-4 w-4" />
                    Business Hours
                  </p>
                  <div className="mt-3 divide-y divide-neutral-100">
                    {BUSINESS_HOURS.map((row) => (
                      <div key={row.day} className="flex items-center justify-between py-2.5">
                        <p className="text-sm font-medium text-neutral-700">{row.day}</p>
                        <p className={`text-sm ${row.hours === "Closed" ? "text-neutral-400" : "text-neutral-500"}`}>
                          {row.hours}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-8">
            <div className="text-center">
              <p className="text-sm font-bold uppercase tracking-widest text-primary">FAQ</p>
              <h2 className="mt-3 text-3xl font-bold text-neutral-900 md:text-4xl">
                Frequently asked questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="mt-12">
              {FAQS.map((faq, i) => (
                <AccordionItem
                  key={faq.q}
                  value={`faq-${i}`}
                  className="mb-3 rounded-xl border border-neutral-200 bg-white px-6 shadow-sm shadow-neutral-900/[0.02]"
                >
                  <AccordionTrigger className="text-left text-base font-semibold text-neutral-900 hover:no-underline">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-neutral-500">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Follow us */}
        <section className="bg-neutral-50 py-24">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-8">
            <h2 className="text-3xl font-bold text-neutral-900 md:text-4xl">Follow us</h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-neutral-500">
              Stay connected with the latest product updates, industry
              insights, and company news.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              {SOCIALS.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    className="flex items-center gap-2.5 rounded-full border border-neutral-200 bg-white px-6 py-3 text-sm font-semibold text-neutral-700 shadow-sm shadow-neutral-900/[0.03] transition-colors hover:border-primary hover:text-primary"
                  >
                    <Icon className="h-4 w-4" />
                    {social.label}
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        <FinalCTA
          title="Ready to experience NEIV?"
          subtitle="See how AI-powered visualization can transform your customer journey."
          primaryLabel="Start Free"
          secondaryLabel="Book a Demo"
        />
      </main>
      <Footer />
    </div>
  );
}
